import { useEffect, useRef } from 'react'
import { notificationScopeInvalidationTags, notificationsApi } from '../api/notifications'
import { store } from '../store/store'
import { handleNotificationEntitySideEffects } from '../utils/notificationEntitySideEffects'
import { useAppDispatch } from './hooks'
import { useGetProfileQuery } from '../api'

/** Backend HEARTBEAT_SECONDS = 20; порог = 2 пропущенных ping + запас. */
const SSE_STALE_MS = 45_000
const HEARTBEAT_CHECK_MS = 10_000

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function invalidateNotificationFeed(dispatch: ReturnType<typeof useAppDispatch>) {
  dispatch(notificationsApi.util.invalidateTags(notificationScopeInvalidationTags))
}

export function useNotificationsSse() {
  const dispatch = useAppDispatch()
  const stoppedRef = useRef(false)
  const esRef = useRef<EventSource | null>(null)
  const forceReconnectRef = useRef<(() => void) | null>(null)
  const profileQuery = useGetProfileQuery()

  useEffect(() => {
    stoppedRef.current = false

    const connectOnce = async () => {
      const result = await dispatch(
        notificationsApi.endpoints.createNotificationStreamToken.initiate(),
      ).unwrap()

      const es = new EventSource(result.stream_url)
      esRef.current = es

      let lastAliveAt = Date.now()
      let heartbeatTimer: ReturnType<typeof setInterval> | null = null

      const touchAlive = () => {
        lastAliveAt = Date.now()
      }

      const cleanup = () => {
        if (heartbeatTimer != null) {
          clearInterval(heartbeatTimer)
          heartbeatTimer = null
        }
        if (esRef.current === es) {
          esRef.current = null
        }
        es.close()
      }

      es.addEventListener('ready', touchAlive)
      es.addEventListener('ping', touchAlive)

      es.addEventListener('notification', (event) => {
        touchAlive()
        invalidateNotificationFeed(dispatch)

        try {
          const raw = (event as MessageEvent).data
          if (typeof raw !== 'string' || raw.length === 0) return
          const parsed: unknown = JSON.parse(raw)
          handleNotificationEntitySideEffects(dispatch, () => store.getState(), parsed)
        } catch {
          // ignore malformed SSE payload
        }
      })

      heartbeatTimer = setInterval(() => {
        if (Date.now() - lastAliveAt >= SSE_STALE_MS) {
          cleanup()
          forceReconnectRef.current?.()
        }
      }, HEARTBEAT_CHECK_MS)

      await new Promise<void>((resolve) => {
        const finish = () => {
          cleanup()
          resolve()
        }

        forceReconnectRef.current = finish
        es.onerror = finish
      })

      forceReconnectRef.current = null
    }

    const run = async () => {
      if (!profileQuery.isSuccess) return

      let attempt = 0

      while (!stoppedRef.current) {
        try {
          await connectOnce()
          attempt = 0
        } catch {
          // ignore and retry with backoff
        }

        if (stoppedRef.current) break

        attempt += 1
        const backoffMs = Math.min(30000, [1000, 2000, 5000, 10000, 20000, 30000][attempt - 1] ?? 30000)
        await sleep(backoffMs)
      }
    }

    run()

    return () => {
      stoppedRef.current = true
      forceReconnectRef.current?.()
      forceReconnectRef.current = null
    }
  }, [dispatch, profileQuery.isSuccess])

  useEffect(() => {
    if (!profileQuery.isSuccess) return

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      invalidateNotificationFeed(dispatch)
      forceReconnectRef.current?.()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [dispatch, profileQuery.isSuccess])
}
